// store/slices/sessionSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

interface SessionEvent {
	id: string;
	actual_items: string;
	items_validated?: boolean;
	[key: string]: any;
}

interface Session {
	id: string;
	title: string;
	start: string;
	end: string;
	surgeon: {
		id: string;
		acronym?: string;
		name: string;
		[key: string]: any;
	};
	hospital: {
		id: string;
		acronym?: string;
		name: string;
		[key: string]: any;
	};
	events: SessionEvent[];
}

interface SessionState {
	selectedSession: Session | null;
	isEditing: boolean;
	originalSession: Session | null;
}

const initialState: SessionState = {
	selectedSession: null,
	isEditing: false,
	originalSession: null,
};

const sessionSlice = createSlice({
	name: "session",
	initialState,
	reducers: {
		setSelectedSession(state, action: PayloadAction<Session>) {
			state.selectedSession = action.payload;
			state.originalSession = { ...action.payload }; // store initial snapshot
		},
		clearSelectedSession(state) {
			state.selectedSession = null;
			state.originalSession = null;
			state.isEditing = false;
		},
		setSessionEditMode(state, action: PayloadAction<boolean>) {
			state.isEditing = action.payload;
		},
		updateSessionField(
			state,
			action: PayloadAction<{ field: keyof Session; value: any }>
		) {
			if (state.selectedSession) {
				const { field, value } = action.payload;
				state.selectedSession[field] = value;
			}
		},
		resetSessionEdits(state) {
			state.selectedSession = state.originalSession
				? { ...state.originalSession }
				: null;
			state.isEditing = false;
		},
		saveSessionEdits(state) {
			state.originalSession = state.selectedSession
				? { ...state.selectedSession }
				: null;
			state.isEditing = false;
		},
		updateSessionEvents(state, action: PayloadAction<SessionEvent[]>) {
			if (state.selectedSession) {
				state.selectedSession.events = action.payload;
			}
		},
	},
});

export const {
	setSelectedSession,
	clearSelectedSession,
	setSessionEditMode,
	updateSessionField,
	resetSessionEdits,
	saveSessionEdits,
	updateSessionEvents,
} = sessionSlice.actions;

export const selectSelectedSession = (state: RootState) =>
	state.session.selectedSession;

export const selectOriginalSession = (state: RootState) =>
	state.session.originalSession;

export const selectSessionEditMode = (state: RootState) =>
	state.session.isEditing;

export default sessionSlice.reducer;
